import React, { memo, useCallback, useEffect } from 'react'

import { CabinClass, TripType } from '@/shared/functions/Flights/flights.types'
import { extractBaseUrl, updateCase } from '@/shared/functions/String/string.functions'
import linkStyles from '@/shared/styles/link.styles'
import { Box, Divider, Tooltip, Typography } from '@mui/material'
import { formatDistanceToNow } from 'date-fns'
import { FormikProps, useFormik } from 'formik'

import FlightSegments from '../FlightSegments/FlightSegments.component'
import Button from '@/shared/components/Button/Button.component'
import Form from '@/shared/components/Form/Form.component'
import Link from '@/shared/components/Link/Link.component'
import Modal from '@/shared/components/Modal/Modal.component'

import styles from '../../ExternalBookings.styles'
import {
  flightDetailsFormSchema,
  initialFlightDetailsValues,
  initialTripDetailsValues,
  tripDetailsFormSchema,
  tripDetailsFormSection
} from './ExternalBookingsModal.dictionary'
import {
  ExternalBookingsModalProps,
  FlightDetailsFormValues,
  TripDetailsFormValues
} from './ExternalBookingsModal.types'

const ExternalBookingsModal: React.FC<ExternalBookingsModalProps> = (props) => {
  const {
    open,
    onClose,
    bookingDetails,
    onSubmit,
    airports,
    airlines,
    denyBookingRequestLoading,
    createItineraryLoading,
    onDeny,
    mode = 'create',
    editTripData,
    onUpdate,
    updateTripLoading = false
  } = props

  const isEditMode = mode === 'edit'
  const isLoading = isEditMode ? updateTripLoading : createItineraryLoading

  const getInitialTripDetailsValues = useCallback((): TripDetailsFormValues => {
    if (isEditMode && editTripData) {
      const tripDetails = editTripData.tripDetails.flightItineraryDetails
      const bookingDetails = editTripData.tripDetails.flightBookingDetails

      return {
        tripType: tripDetails.tripType as TripType,
        cabinClass: tripDetails.cabinClass as CabinClass,
        tripPrice: bookingDetails?.flightCost?.toString() || '0',
        tripCurrency: bookingDetails?.currency || 'USD'
      }
    } else {
      return initialTripDetailsValues
    }
  }, [isEditMode, editTripData])

  const getInitialFlightDetailsValues = useCallback((): FlightDetailsFormValues => {
    if (isEditMode && editTripData?.tripDetails.flightBookingDetails?.flights) {
      const flights = editTripData.tripDetails.flightBookingDetails.flights
      const legs = editTripData.tripDetails.flightItineraryDetails.legs

      // Group flights by leg/slice
      const slices = legs.map(() => {
        const legFlights = flights.filter(() => true) // Simplified - in real implementation, you'd match flights to legs

        return {
          segments: legFlights.map((flight) => {
            // Find the airline ID that matches the airline code from the existing flight data
            const matchingAirline = airlines.find(
              (airline) => airline.label.includes(flight.airline?.name || '') || airline.value === flight.airline?.code
            )

            return {
              flightNumber: flight.flightNumber,
              airlineId: matchingAirline?.value || '',
              departureTime: flight.departureTime,
              arrivalTime: flight.arrivalTime,
              originAirportIataCode: flight.origin.airport.iataCode,
              destinationAirportIataCode: flight.destination.airport.iataCode
            }
          })
        }
      })

      return { slices }
    } else {
      return initialFlightDetailsValues
    }
  }, [isEditMode, editTripData, airlines])

  const tripDetailsFormik: FormikProps<TripDetailsFormValues> = useFormik({
    initialValues: getInitialTripDetailsValues(),
    validationSchema: tripDetailsFormSchema,
    enableReinitialize: true,
    onSubmit: () => {
      // Do nothing
    }
  })

  const flightDetailsFormik: FormikProps<FlightDetailsFormValues> = useFormik({
    initialValues: getInitialFlightDetailsValues(),
    validationSchema: flightDetailsFormSchema,
    enableReinitialize: true,
    onSubmit: () => {
      // Do nothing
    }
  })

  useEffect(() => {
    if (isEditMode && editTripData && airlines.length > 0) {
      tripDetailsFormik.setValues(getInitialTripDetailsValues())
      flightDetailsFormik.setValues(getInitialFlightDetailsValues())
    } else if (!isEditMode && bookingDetails?.airlineBookingDetails?.airline) {
      const airlineId = bookingDetails.airlineBookingDetails.airline.id

      initialFlightDetailsValues.slices.forEach((slice) => {
        slice.segments.forEach((segment) => {
          segment.airlineId = airlineId
        })
      })

      flightDetailsFormik.setValues(initialFlightDetailsValues)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingDetails, isEditMode, editTripData, airlines])

  const handleCloseModal = useCallback(() => {
    tripDetailsFormik.resetForm()
    flightDetailsFormik.resetForm()
    onClose()
  }, [onClose, tripDetailsFormik, flightDetailsFormik])

  const handleTripDetailsFormChange = useCallback(
    (formik: Partial<FormikProps<unknown>>) => {
      const currentValues = tripDetailsFormik.values
      const updatedValues = formik.values as TripDetailsFormValues

      if (JSON.stringify(currentValues) !== JSON.stringify(updatedValues)) {
        tripDetailsFormik.setValues(updatedValues)
      }
    },
    [tripDetailsFormik]
  )

  const isFormValid = useCallback(() => {
    // Check trip details form
    const isTripDetailsFormValid =
      tripDetailsFormik.isValid &&
      Object.keys(tripDetailsFormik.errors).length === 0 &&
      tripDetailsFormik.values.tripType &&
      tripDetailsFormik.values.cabinClass &&
      tripDetailsFormik.values.tripPrice &&
      tripDetailsFormik.values.tripCurrency

    // Check flight details form - validate nested segments
    const isFlightDetailsFormValid =
      flightDetailsFormik.isValid &&
      Object.keys(flightDetailsFormik.errors).length === 0 &&
      flightDetailsFormik.values.slices.length > 0 &&
      flightDetailsFormik.values.slices.every(
        (slice) =>
          slice.segments.length > 0 &&
          slice.segments.every(
            (segment) =>
              segment.flightNumber &&
              segment.airlineId &&
              segment.departureTime &&
              segment.arrivalTime &&
              segment.originAirportIataCode &&
              segment.destinationAirportIataCode
          )
      )

    return isTripDetailsFormValid && isFlightDetailsFormValid
  }, [tripDetailsFormik, flightDetailsFormik])

  const handleSubmit = useCallback(() => {
    const formValues = {
      tripType: tripDetailsFormik.values.tripType,
      cabinClass: tripDetailsFormik.values.cabinClass,
      tripPrice: tripDetailsFormik.values.tripPrice,
      tripCurrency: tripDetailsFormik.values.tripCurrency,
      slices: flightDetailsFormik.values.slices.map((slice) => ({
        originAirportIataCode: slice.segments[0].originAirportIataCode,
        destinationAirportIataCode: slice.segments[0].destinationAirportIataCode,
        travelDate: slice.segments[0].departureTime,
        segments: slice.segments.map((segment) => ({
          flightNumber: segment.flightNumber,
          airlineId: segment.airlineId,
          departureTime: segment.departureTime,
          arrivalTime: segment.arrivalTime,
          originAirportIataCode: segment.originAirportIataCode,
          destinationAirportIataCode: segment.destinationAirportIataCode
        }))
      }))
    }

    if (isEditMode && onUpdate) {
      onUpdate(formValues)
    } else {
      onSubmit(formValues)
    }
  }, [tripDetailsFormik, flightDetailsFormik, onSubmit, onUpdate, isEditMode])

  const handleDeny = useCallback(() => {
    onDeny()
  }, [onDeny])

  // Helper functions for formatting time metrics
  const formatTimeRemaining = useCallback((expiresAt: string): string => {
    const expiry = new Date(expiresAt)
    const now = new Date()

    if (expiry.getTime() <= now.getTime()) {
      return 'Expired'
    }

    return formatDistanceToNow(expiry, { addSuffix: true })
  }, [])

  const formatTimeUntilDeletion = useCallback((deletionDate: string): string => {
    const deletion = new Date(deletionDate)
    const now = new Date()

    if (deletion.getTime() <= now.getTime()) {
      return 'Scheduled for deletion'
    }

    return formatDistanceToNow(deletion, { addSuffix: true })
  }, [])

  const getModalHeader = useCallback(() => {
    if (isEditMode && editTripData) {
      const traveler = editTripData.traveler
      const tripDetails = editTripData.tripDetails.flightItineraryDetails

      const rows = [
        {
          label: 'Traveler',
          value: `${traveler.firstName} ${traveler.lastName}`
        },
        {
          label: 'Trip ID',
          value: tripDetails.id.toString()
        },
        {
          label: 'Booking Type',
          value: updateCase(tripDetails.itinerarySource, 'title')
        },
        {
          label: 'Status',
          value: updateCase(tripDetails.status.displayName, 'title')
        }
      ]

      return (
        <Box sx={styles.modalInfo}>
          {rows.map((row, index) => (
            <Box key={index} sx={styles.infoRow}>
              <Typography variant="body2" sx={styles.infoLabel}>
                {row.label}
              </Typography>

              <Typography variant="body2" sx={styles.infoValue}>
                {row.value}
              </Typography>
            </Box>
          ))}
        </Box>
      )
    }

    if (!bookingDetails) return null

    if (bookingDetails.providerBookingType === 'airline') {
      const airlineWebsite = bookingDetails.airlineBookingDetails?.airline?.conditionsOfCarriageUrl
      const airlineWebsiteBaseUrl = airlineWebsite ? extractBaseUrl(airlineWebsite) : undefined
      const airlineName = bookingDetails.airlineBookingDetails?.airline?.name
      let airlineWebsiteLink = null

      if (airlineWebsiteBaseUrl) {
        airlineWebsiteLink = (
          <Link href={airlineWebsiteBaseUrl} sx={linkStyles} target="_blank" rel="noopener noreferrer">
            {airlineName} →
          </Link>
        )
      }

      const rows = [
        {
          label: 'Traveler',
          value: `${bookingDetails.traveler.firstName} ${bookingDetails.traveler.lastName}`
        },
        {
          label: 'Source',
          value: airlineWebsiteLink ?? airlineName
        },
        {
          label: 'Ticket Number',
          value: bookingDetails.airlineBookingDetails?.ticketNumber ?? 'N/A'
        },
        {
          label: 'Confirmation Code',
          value: bookingDetails.airlineBookingDetails?.confirmationCode ?? 'N/A'
        }
      ]

      // Add PDF link if available
      if (bookingDetails.pdfUrl) {
        // Build tooltip text
        const tooltipLines = []
        if (bookingDetails.pdfExpiresAt) {
          tooltipLines.push(`Admin link expires ${formatTimeRemaining(bookingDetails.pdfExpiresAt)}`)
        }
        if (bookingDetails.pdfScheduledDeletionDate) {
          tooltipLines.push(`File deletion ${formatTimeUntilDeletion(bookingDetails.pdfScheduledDeletionDate)}`)
        }
        const tooltipText = tooltipLines.join(' • ')

        const pdfLink = (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
            <Tooltip title={tooltipText} placement="top" arrow>
              <Link href={bookingDetails.pdfUrl} sx={linkStyles} target="_blank" rel="noopener noreferrer">
                View PDF Document →
              </Link>
            </Tooltip>
          </Box>
        )
        rows.push({
          label: 'PDF Document',
          value: pdfLink
        })

        // Add AI feedback if available
        if (bookingDetails.aiFeedback) {
          const aiFeedbackValue = (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
              <Typography variant="body2" sx={{ textAlign: 'right' }}>
                {bookingDetails.aiFeedback}
              </Typography>
            </Box>
          )
          rows.push({
            label: 'Intelligent Document Assessment',
            value: aiFeedbackValue
          })
        }
      }

      return (
        <Box sx={styles.modalInfo}>
          {rows.map((row, index) => (
            <Box key={index} sx={styles.infoRow}>
              <Typography variant="body2" sx={styles.infoLabel}>
                {row.label}
              </Typography>

              <Typography variant="body2" sx={styles.infoValue}>
                {row.value}
              </Typography>
            </Box>
          ))}
        </Box>
      )
    } else {
      const travelAgencyWebsite = bookingDetails.travelAgencyBookingDetails?.travelAgency?.website
      const travelAgencyName = bookingDetails.travelAgencyBookingDetails?.travelAgency?.name
      let travelAgencyWebsiteLink = null

      if (travelAgencyWebsite) {
        travelAgencyWebsiteLink = (
          <Link href={travelAgencyWebsite} sx={linkStyles} target="_blank" rel="noopener noreferrer">
            {travelAgencyName} →
          </Link>
        )
      }

      const rows = [
        {
          label: 'Traveler',
          value: `${bookingDetails.traveler.firstName} ${bookingDetails.traveler.lastName}`
        },
        {
          label: 'Source',
          value: travelAgencyWebsiteLink ?? travelAgencyName
        },
        {
          label: 'Itinerary Reference Number',
          value: bookingDetails.travelAgencyBookingDetails?.itineraryReferenceNumber ?? 'N/A'
        },
        {
          label: 'Email',
          value: bookingDetails.travelAgencyBookingDetails?.email ?? 'N/A'
        }
      ]

      // Add PDF link if available
      if (bookingDetails.pdfUrl) {
        // Build tooltip text
        const tooltipLines = []
        if (bookingDetails.pdfExpiresAt) {
          tooltipLines.push(`Admin link expires ${formatTimeRemaining(bookingDetails.pdfExpiresAt)}`)
        }
        if (bookingDetails.pdfScheduledDeletionDate) {
          tooltipLines.push(`File deletion ${formatTimeUntilDeletion(bookingDetails.pdfScheduledDeletionDate)}`)
        }
        const tooltipText = tooltipLines.join(' • ')

        const pdfLink = (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
            <Tooltip title={tooltipText} placement="top" arrow>
              <Link href={bookingDetails.pdfUrl} sx={linkStyles} target="_blank" rel="noopener noreferrer">
                View PDF Document →
              </Link>
            </Tooltip>
          </Box>
        )
        rows.push({
          label: 'PDF Document',
          value: pdfLink
        })

        // Add AI feedback if available
        if (bookingDetails.aiFeedback) {
          const aiFeedbackValue = (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
              <Typography variant="body2" sx={{ textAlign: 'right' }}>
                {bookingDetails.aiFeedback}
              </Typography>
            </Box>
          )
          rows.push({
            label: 'Intelligent Document Assessment',
            value: aiFeedbackValue
          })
        }
      }

      return (
        <Box sx={styles.modalInfo}>
          {rows.map((row, index) => (
            <Box key={index} sx={styles.infoRow}>
              <Typography variant="body2" sx={styles.infoLabel}>
                {row.label}
              </Typography>

              <Typography variant="body2" sx={styles.infoValue}>
                {row.value}
              </Typography>
            </Box>
          ))}
        </Box>
      )
    }
  }, [bookingDetails, isEditMode, editTripData, formatTimeRemaining, formatTimeUntilDeletion])

  const getModalContent = useCallback(() => {
    if (!bookingDetails && !isEditMode) return null

    return (
      <Box sx={styles.modalContent}>
        {getModalHeader()}

        <Divider />

        <Box sx={{ mt: 3 }}>
          <Form
            initialValues={getInitialTripDetailsValues()}
            hideButtons
            validationSchema={tripDetailsFormSchema}
            onSubmit={handleSubmit}
            onFormChange={(formik) => handleTripDetailsFormChange(formik)}
            formSections={[tripDetailsFormSection]}
          />

          {flightDetailsFormik && (
            <Box sx={styles.modalContent}>
              <Typography variant="h6" sx={styles.modalTitle}>
                Flight Details
              </Typography>

              <FlightSegments
                formik={flightDetailsFormik}
                airports={airports}
                airlines={airlines}
                tripType={tripDetailsFormik.values.tripType}
                airlineInBookingRequest={bookingDetails?.airlineBookingDetails?.airline}
              />
            </Box>
          )}
        </Box>
      </Box>
    )
  }, [
    bookingDetails,
    isEditMode,
    flightDetailsFormik,
    handleSubmit,
    airports,
    airlines,
    getModalHeader,
    handleTripDetailsFormChange,
    tripDetailsFormik.values.tripType,
    getInitialTripDetailsValues
  ])

  const getModalTitle = () => {
    return isEditMode ? 'Edit External Flight Itinerary' : 'Process External Flight Booking Requests'
  }

  const getModalSubtitle = () => {
    return isEditMode ? 'Update flight details for this itinerary' : 'Enter flight details to create an itinerary'
  }

  const getPrimaryButtonLabel = () => {
    return isEditMode ? 'Save Changes' : 'Process Booking'
  }

  const getTooltipText = () => {
    if (isFormValid()) return undefined

    return isEditMode
      ? 'You must fill in all fields to save changes'
      : 'You must fill in all fields to process the booking'
  }

  return (
    <Modal
      open={open}
      onClose={handleCloseModal}
      maxWidth="md"
      title={getModalTitle()}
      subtitle={getModalSubtitle()}
      headerRightSection={
        !isEditMode ? (
          <Button text="Deny" loading={denyBookingRequestLoading} onClick={handleDeny} buttonType="error" />
        ) : undefined
      }
      body={getModalContent()}
      primaryButton={{
        label: getPrimaryButtonLabel(),
        loading: isLoading,
        tooltipText: getTooltipText(),
        disabled: !isFormValid(),
        onClick: handleSubmit
      }}
      secondaryButton={{
        label: 'Cancel',
        onClick: handleCloseModal
      }}
    />
  )
}

export default memo(ExternalBookingsModal)
